import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteProductPreviewApi,
  getAllProductPreviewApi,
  getProductPreviewApi,
  postProductPreviewApi,
  updateProductPreviewApi,
} from "../../api/ecommerce/ProductPreviewApi";

const initialState = {
  productPreview: null,
  productPreviews: [],
  loading: false,
  detailsLoading: false,
  saving: false,
  deleting: false,
  error: null,
  success: false,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.data?.message ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

export const postProductPreview = createAsyncThunk(
  "productPreview/postProductPreview",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postProductPreviewApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const updateProductPreview = createAsyncThunk(
  "productPreview/updateProductPreview",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProductPreviewApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const deleteProductPreview = createAsyncThunk(
  "productPreview/deleteProductPreview",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteProductPreviewApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getProductPreview = createAsyncThunk(
  "productPreview/getProductPreview",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getProductPreviewApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getAllProductPreview = createAsyncThunk(
  "productPreview/getAllProductPreview",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getAllProductPreviewApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

const productPreviewSlice = createSlice({
  name: "productPreview",
  initialState,
  reducers: {
    clearProductPreviewError: (state) => {
      state.error = null;
    },
    clearProductPreviewState: (state) => {
      state.productPreview = null;
      state.productPreviews = [];
      state.loading = false;
      state.detailsLoading = false;
      state.saving = false;
      state.deleting = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductPreview.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getProductPreview.fulfilled, (state, action) => {
        state.productPreview = action?.payload?.response ?? action?.payload;
        state.detailsLoading = false;
        state.success = true;
      })
      .addCase(getProductPreview.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(getAllProductPreview.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getAllProductPreview.fulfilled, (state, action) => {
        state.productPreviews = action?.payload?.response ?? action?.payload ?? [];
        state.loading = false;
        state.success = true;
      })
      .addCase(getAllProductPreview.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(postProductPreview.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(postProductPreview.fulfilled, (state, action) => {
        state.productPreview = action?.payload?.response ?? action?.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(postProductPreview.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(updateProductPreview.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateProductPreview.fulfilled, (state, action) => {
        state.productPreview = action?.payload?.response ?? action?.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(updateProductPreview.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(deleteProductPreview.pending, (state) => {
        state.deleting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteProductPreview.fulfilled, (state) => {
        state.deleting = false;
        state.success = true;
      })
      .addCase(deleteProductPreview.rejected, (state, action) => {
        state.deleting = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      });
  },
});

export const { clearProductPreviewError, clearProductPreviewState } =
  productPreviewSlice.actions;

export default productPreviewSlice.reducer;
